<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useUserStore } from '@/store/user-store';
import Chart from "primevue/chart";

const props = defineProps({
  sectorExposure: {
    type: Object,
    default: () => ({})
  }
});

const store = useUserStore();

// State management
const benchmarkData = ref(null);
const isLoading = ref(false);
const activeSectorTab = ref('Nifty50');
const activeTimeframeTab = ref('All');
const chartData = ref({});
const chartOptions = ref({});

// Cache management
const CACHE_KEY = 'sector_benchmark_data';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Computed sector tabs based on portfolio exposure
const sectorTabs = computed(() => {
  const tabs = [];

  // Always check Nifty50 first
  const nifty50Data = benchmarkData.value?.responseData?.find(
    item => item.sectorData.sector === 'Nifty50'
  );

  if (nifty50Data && nifty50Data.sectorData.historicalData?.length > 0) {
    tabs.push({ key: 'Nifty50', label: 'Nifty 50', isDefault: true });
  }

  // Check other sectors from portfolio exposure
  if (props.sectorExposure && Object.keys(props.sectorExposure).length > 0) {
    Object.keys(props.sectorExposure).forEach(sector => {
      // Find if this sector has data in benchmarkData
      const sectorData = benchmarkData.value?.responseData?.find(
        item => item.sectorData.sector === sector
      );

      // Only add if historical data exists and has at least one entry
      if (sectorData && sectorData.sectorData.historicalData?.length > 0) {
        tabs.push({
          key: sector,
          label: sector,
          isDefault: false
        });
      }
    });
  }

  return tabs;
});

// Timeframe tabs
const timeframeTabs = [
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: 'All', label: 'All', days: null }
];

// Chart options for blue-themed line charts
const getChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
        color: '#f0f0f0'
      },
      ticks: {
        font: {
          size: 11
        },
        maxTicksLimit: 6,
        color: '#666666'
      },
      border: {
        display: true,
        color: '#e0e0e0'
      }
    },
    y: {
      display: true,
      grid: {
        display: true,
        color: '#f0f0f0'
      },
      ticks: {
        font: {
          size: 11
        },
        color: '#666666',
        callback: function (value) {
          return `₹${value.toLocaleString('en-IN')}`;
        }
      },
      border: {
        display: false
      }
    }
  },
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: '#2196F3',
      borderWidth: 1,
      cornerRadius: 6,
      displayColors: false,
      mode: 'index',
      intersect: false,
      callbacks: {
        title: function(context) {
          return `Date: ${context[0].label}`;
        },
        label: function(context) {
          const value = context.parsed.y;
          return `${activeSectorTab.value}: ₹${value.toLocaleString('en-IN')}`;
        }
      }
    }
  },
  elements: {
    line: {
      borderColor: '#2196F3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      borderWidth: 2,
      tension: 0.4,
      fill: false
    },
    point: {
      backgroundColor: '#2196F3',
      borderColor: '#ffffff',
      borderWidth: 2,
      radius: 3,
      hoverRadius: 6,
      hoverBackgroundColor: '#1976D2',
      hoverBorderColor: '#ffffff',
      hoverBorderWidth: 2
    }
  }
});

// Cache utilities
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

const setCachedData = (data) => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

const isCacheExpired = (timestamp) => {
  return Date.now() - timestamp > CACHE_DURATION;
};

// Generate sector string for API call
const generateSectorString = () => {
  const sectors = ['Nifty50'];
  if (props.sectorExposure && Object.keys(props.sectorExposure).length > 0) {
    sectors.push(...Object.keys(props.sectorExposure));
  }
  return sectors.join(',');
};

// Load benchmark data with caching
const loadBenchmarkData = async () => {
  if (isLoading.value) return;

  isLoading.value = true;

  try {
    // Check cache first
    const cached = getCachedData();
    if (cached && !isCacheExpired(cached.timestamp)) {
      console.log('Using cached benchmark data');
      benchmarkData.value = cached.data;
      ensureValidActiveTab();
      formatChartData();
      isLoading.value = false;
      return;
    }

    console.log('Fetching fresh benchmark data');
    const sectorString = generateSectorString();
    const data = await store.getBenchmarkData(sectorString);
    console.log('Fetched benchmark data:', data);
    benchmarkData.value = data;
    setCachedData(data);
    ensureValidActiveTab();
    formatChartData();
  } catch (error) {
    console.error('Error loading benchmark data:', error);
    // You could show an error state here
  } finally {
    isLoading.value = false;
  }
};

// Filter data based on selected timeframe using actual dates
const filterDataByTimeframe = (historicalData, timeframeDays) => {
  if (!historicalData || !Array.isArray(historicalData)) return [];
  if (timeframeDays === null) return historicalData; // All data

  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - timeframeDays);

  // Filter by actual dates from the API response
  return historicalData.filter(item => {
    if (!item.date) return false;
    const itemDate = new Date(item.date);
    return itemDate >= cutoffDate;
  });
};

// Format chart data for the active sector and timeframe
// Expects API data structure: { date: "YYYY-MM-DD", close: "price.value" }
const formatChartData = () => {
  if (!benchmarkData.value?.responseData) {
    chartData.value = {};
    return;
  }

  const sectorData = benchmarkData.value.responseData.find(
    item => item.sectorData.sector === activeSectorTab.value
  );

  if (!sectorData) {
    chartData.value = {};
    return;
  }

  const timeframeDays = timeframeTabs.find(t => t.key === activeTimeframeTab.value)?.days;
  const filteredData = filterDataByTimeframe(sectorData.sectorData.historicalData, timeframeDays);

  // Sort by date to ensure chronological order (oldest to newest)
  const sortedData = [...filteredData].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Extract actual dates and prices as tuples
  const labels = sortedData.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short'
    });
  });

  const prices = sortedData.map(item => parseFloat(item.close));

  chartData.value = {
    labels,
    datasets: [{
      data: prices,
      borderColor: '#2196F3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      borderWidth: 2,
      tension: 0.4,
      fill: false,
      pointBackgroundColor: '#2196F3',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: '#1976D2',
      pointHoverBorderColor: '#ffffff'
    }]
  };

  chartOptions.value = getChartOptions();
};

// Handle tab changes
const handleSectorTabChange = (sectorKey) => {
  activeSectorTab.value = sectorKey;
  formatChartData();
};

const handleTimeframeTabChange = (timeframeKey) => {
  activeTimeframeTab.value = timeframeKey;
  formatChartData();
};

// Ensure active tab is valid after data loads
const ensureValidActiveTab = () => {
  const availableTabs = sectorTabs.value;
  if (availableTabs.length === 0) return;

  // Check if current active tab is available
  const isActiveTabAvailable = availableTabs.some(tab => tab.key === activeSectorTab.value);

  // If not available, switch to first available tab (usually Nifty50)
  if (!isActiveTabAvailable) {
    activeSectorTab.value = availableTabs[0].key;
  }
};

// Get current sector price (latest value by date)
const getCurrentPrice = () => {
  if (!benchmarkData.value?.responseData) return 'N/A';

  const sectorData = benchmarkData.value.responseData.find(
    item => item.sectorData.sector === activeSectorTab.value
  );

  if (!sectorData || !sectorData.sectorData.historicalData?.length) return 'N/A';

  // Find the most recent data by sorting by date and taking the last item
  const sortedByDate = [...sectorData.sectorData.historicalData].sort((a, b) => new Date(a.date) - new Date(b.date));
  const latestData = sortedByDate[sortedByDate.length - 1];

  return `₹${parseFloat(latestData.close).toLocaleString('en-IN')}`;
};

// Calculate gain/loss percentage for selected timeframe
const getGainLossPercentage = () => {
  if (!benchmarkData.value?.responseData) return { percentage: 'N/A', isGain: true };

  const sectorData = benchmarkData.value.responseData.find(
    item => item.sectorData.sector === activeSectorTab.value
  );

  if (!sectorData || !sectorData.sectorData.historicalData?.length) return { percentage: 'N/A', isGain: true };

  // Get filtered data based on timeframe
  const timeframeDays = timeframeTabs.find(t => t.key === activeTimeframeTab.value)?.days;
  const filteredData = filterDataByTimeframe(sectorData.sectorData.historicalData, timeframeDays);

  if (filteredData.length < 2) return { percentage: 'N/A', isGain: true };

  // Sort by date to ensure chronological order
  const sortedData = [...filteredData].sort((a, b) => new Date(a.date) - new Date(b.date));

  const beginValue = parseFloat(sortedData[0].close);
  const endValue = parseFloat(sortedData[sortedData.length - 1].close);

  const percentageChange = ((endValue / beginValue - 1) * 100);
  const isGain = percentageChange >= 0;

  return {
    percentage: `${isGain ? '+' : ''}${percentageChange.toFixed(2)}%`,
    isGain: isGain
  };
};

// Watch for sector exposure changes
watch(() => props.sectorExposure, (newValue) => {
  if (newValue && Object.keys(newValue).length > 0 && !benchmarkData.value) {
    // Delay loading to avoid blocking initial render
    nextTick(() => {
      setTimeout(() => {
        loadBenchmarkData();
      }, 1500); // 1.5 second delay for better UX
    });
  }
}, { immediate: true });

// Watch for active tab changes
watch([activeSectorTab, activeTimeframeTab], () => {
  if (benchmarkData.value) {
    formatChartData();
  }
});

onMounted(() => {
  // Load data if sector exposure is already available
  if (props.sectorExposure && Object.keys(props.sectorExposure).length > 0) {
    setTimeout(() => {
      loadBenchmarkData();
    }, 1500);
  }
});
</script>

<template>
  <div class="sector-benchmark-container">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">Loading sector benchmark data...</p>
      </div>
    </div>

    <!-- Chart Content -->
    <div v-else-if="benchmarkData" class="chart-content">
      <!-- Header -->
      <div class="chart-header">
        <h4 class="chart-title">Benchmarks' Historical Data</h4>
        <div class="current-price">
          {{ getCurrentPrice() }}
        </div>
      </div>

      <!-- Sector Tabs -->
      <div class="sector-tabs">
        <button
          v-for="sector in sectorTabs"
          :key="sector.key"
          class="sector-tab-button"
          :class="{ 'active': activeSectorTab === sector.key }"
          @click="handleSectorTabChange(sector.key)"
        >
          {{ sector.label }}
        </button>
      </div>

      <!-- Timeframe Tabs with Performance -->
      <div class="timeframe-container">
        <div class="timeframe-tabs">
          <button
            v-for="timeframe in timeframeTabs"
            :key="timeframe.key"
            class="timeframe-tab-button"
            :class="{ 'active': activeTimeframeTab === timeframe.key }"
            @click="handleTimeframeTabChange(timeframe.key)"
          >
            {{ timeframe.label }}
          </button>
        </div>

        <!-- Inline Gain/Loss Display -->
        <div v-if="chartData.labels && chartData.labels.length > 0" class="inline-performance">
          <span
            class="performance-value"
            :class="{ 'gain': getGainLossPercentage().isGain, 'loss': !getGainLossPercentage().isGain }"
          >
            {{ getGainLossPercentage().percentage }}
          </span>
        </div>
      </div>

      <!-- Chart -->
      <div class="chart-wrapper">
        <Chart
          v-if="chartData.labels && chartData.labels.length > 0"
          type="line"
          :data="chartData"
          :options="chartOptions"
          class="benchmark-chart"
        />
        <div v-else class="no-data-message">
          No data available for {{ activeSectorTab }} ({{ activeTimeframeTab }})
        </div>
      </div>
    </div>

    <!-- Placeholder State -->
    <div v-else class="placeholder-state">
      <div class="placeholder-content">
        <div class="placeholder-icon">📈</div>
        <h4 class="placeholder-title">Sector Performance Charts</h4>
        <p class="placeholder-message">
          Historical performance data will load automatically
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sector-benchmark-container {
  background: white;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #2196F3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 14px;
  color: var(--an-text-muted);
  margin: 0;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Chart Content */
.chart-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  min-width: 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.chart-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
}

.current-price {
  font-size: 1rem;
  font-weight: 700;
  color: #2196F3;
  background: rgba(33, 150, 243, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
}

/* Sector Tabs */
.sector-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.sector-tab-button {
  padding: 8px 14px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--an-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
}

.sector-tab-button:hover {
  background: #e9ecef;
  color: var(--an-text-primary);
}

.sector-tab-button.active {
  background: #2196F3;
  color: white;
  border-color: #2196F3;
}

/* Timeframe Container */
.timeframe-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
}

/* Timeframe Tabs */
.timeframe-tabs {
  display: flex;
  gap: 2px;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 2px;
  width: fit-content;
}

.timeframe-tab-button {
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
}

.timeframe-tab-button {
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--an-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
}

.timeframe-tab-button:hover {
  background: rgba(255, 255, 255, 0.7);
  color: var(--an-text-primary);
}

.timeframe-tab-button.active {
  background: white;
  color: #2196F3;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Inline Performance Display */
.inline-performance {
  display: flex;
  align-items: center;
}

.performance-value {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 4px;
  min-width: 40px;
  text-align: center;
  background: transparent;
  border: none;
}

.performance-value.gain {
  color: #28a745;
  background: rgba(40, 167, 69, 0.1);
  border-color: rgba(40, 167, 69, 0.3);
}

.performance-value.loss {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
  border-color: rgba(220, 53, 69, 0.3);
}

/* Chart Wrapper */
.chart-wrapper {
  flex: 1;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.benchmark-chart {
  height: 100%;
  min-height: 250px;
  width: 100%;
  max-width: 100%;
}

.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: var(--an-text-muted);
  font-style: italic;
}

/* Placeholder State */
.placeholder-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.placeholder-content {
  text-align: center;
}

.placeholder-icon {
  font-size: 2rem;
  margin-bottom: 8px;
}

.placeholder-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.placeholder-message {
  font-size: 0.9rem;
  color: var(--an-text-muted);
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .timeframe-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .performance-value {
    font-size: 10px;
    min-width: 35px;
    padding: 4px 8px;
  }

  .sector-tabs {
    gap: 2px;
  }

  .sector-tab-button {
    font-size: 11px;
    padding: 4px 8px;
  }

  .timeframe-tab-button {
    font-size: 10px;
    padding: 4px 8px;
    min-width: 35px;
  }

  .benchmark-chart {
    min-height: 200px;
  }
}
</style>
